// src/components/AdminPage.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { API_BASE as API } from "../utils/apiBase";
import { formatDateForTimeZone, getBrowserTimeZone, isValidTimeZone } from "../utils/timezones";

function cx(...c) {
  return c.filter(Boolean).join(" ");
}

function Badge({ tone = "gray", children }) {
  const tones = {
    gray: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200",
    red: "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-300",
    green: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300",
    blue: "bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300",
    amber: "bg-amber-100 dark:bg-gray-900 text-amber-900 dark:text-amber-200",
    purple: "bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-300",
    teal: "bg-teal-100 dark:bg-teal-950/40 text-teal-800 dark:text-teal-300",
  };
  return (
    <span className={cx("inline-block text-[11px] px-2 py-0.5 rounded", tones[tone] || tones.gray)}>
      {children}
    </span>
  );
}

// All admin timestamps are localized to the signed-in admin's timezone via this
// context (their account time_zone, or the browser zone when unset) - never the
// browser default of a different device or any customer's zone. Set in AdminPage.
const AdminTimeZoneContext = createContext("UTC");

// Hook returning a date formatter bound to the admin's timezone. Every tab/modal
// calls this so times render consistently in the viewer's zone, with the zone
// abbreviation (e.g. CDT) appended so it is unambiguous which zone is shown.
function useFmtDate() {
  const tz = useContext(AdminTimeZoneContext);
  return useCallback(
    (v) => (v ? formatDateForTimeZone(v, tz, { timeZoneName: "short" }) || "-" : "-"),
    [tz]
  );
}

export default function AdminPage({ token, onSessionExpired, viewerTimeZone = "" }) {
  const [tab, setTab] = useState("dashboard");

  // Prefer the admin's saved account timezone; fall back to the browser zone (never
  // silently to UTC) when it is unset or still the default.
  const effectiveTimeZone = useMemo(() => {
    const account = String(viewerTimeZone || "").trim();
    if (account && account !== "UTC" && isValidTimeZone(account)) return account;
    return getBrowserTimeZone() || "UTC";
  }, [viewerTimeZone]);

  const authFetch = useCallback(
    async (path, opts = {}) => {
      const res = await fetch(`${API}${path}`, {
        ...opts,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(opts.headers || {}),
        },
        cache: "no-store",
      });
      if (res.status === 401) {
        onSessionExpired?.("/login");
        throw new Error("unauthorized");
      }
      return res;
    },
    [token, onSessionExpired]
  );

  return (
    <AdminTimeZoneContext.Provider value={effectiveTimeZone}>
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Admin</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Manage users, moderation, and site controls.</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">All times are shown in your timezone ({effectiveTimeZone}).</p>
        </div>

        <div className="mb-5 flex flex-wrap gap-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-1">
          {[
            { key: "dashboard", label: "Dashboard" },
            { key: "users", label: "Users" },
            { key: "reports", label: "Reports" },
            { key: "subscriptions", label: "Subscriptions" },
            { key: "ops", label: "Ops" },
            { key: "audit", label: "Audit Log" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cx(
                "px-3 py-1.5 rounded-lg text-sm font-medium",
                tab === t.key ? "bg-gray-900 text-white" : "text-gray-600 dark:text-gray-300 hover:text-gray-900"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" ? (
          <DashboardTab authFetch={authFetch} />
        ) : tab === "users" ? (
          <UsersTab authFetch={authFetch} />
        ) : tab === "reports" ? (
          <ReportsTab authFetch={authFetch} />
        ) : tab === "subscriptions" ? (
          <SubscriptionsTab authFetch={authFetch} />
        ) : tab === "ops" ? (
          <OpsTab authFetch={authFetch} />
        ) : (
          <AuditTab authFetch={authFetch} />
        )}
      </div>
    </div>
    </AdminTimeZoneContext.Provider>
  );
}

function UsersTab({ authFetch }) {
  const fmtDate = useFmtDate();
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [deleted, setDeleted] = useState("hide");
  const [demo, setDemo] = useState("all");
  const [admins, setAdmins] = useState("");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: pageSize });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (appliedQ.trim()) params.set("q", appliedQ.trim());
      if (type) params.set("type", type);
      if (status) params.set("status", status);
      if (deleted) params.set("deleted", deleted);
      if (demo) params.set("demo", demo);
      if (admins) params.set("admins", admins);
      if (sort) params.set("sort", sort);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const res = await authFetch(`/admin/users?${params.toString()}`);
      if (!res.ok) {
        setError(res.status === 403 ? "Admin access required." : "Could not load users.");
        setData({ items: [], total: 0, page: 1, page_size: pageSize });
        return;
      }
      setData(await res.json());
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [authFetch, appliedQ, type, status, deleted, demo, admins, sort, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div>
      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setAppliedQ(q);
          }}
          className="flex-1 min-w-[200px]"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email, name, or shop name…"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
          />
        </form>
        <select
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
        >
          <option value="">All types</option>
          <option value="shop">Shops</option>
          <option value="customer">Customers</option>
        </select>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="unverified">Unverified</option>
          <option value="banned">Banned</option>
        </select>
        <select
          value={deleted}
          onChange={(e) => {
            setDeleted(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
        >
          <option value="hide">Hide deleted</option>
          <option value="all">Include deleted</option>
          <option value="only">Deleted only</option>
        </select>
        {/* "Demo only" is the quickest way to see whether gc_demo_sessions.py is keeping up:
            anything older than the sweep window still listed here means the job is not running. */}
        <select
          value={demo}
          onChange={(e) => {
            setDemo(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
          aria-label="Demo accounts"
        >
          <option value="all">Include demo</option>
          <option value="hide">Hide demo</option>
          <option value="only">Demo only</option>
        </select>
        <select
          value={admins}
          onChange={(e) => {
            setAdmins(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
        >
          <option value="">Anyone</option>
          <option value="true">Admins only</option>
        </select>
        <select
          value={sort}
          onChange={(e) => {
            setSort(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
        >
          <option value="recent">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name (A-Z)</option>
          <option value="status">Status</option>
        </select>
      </div>

      {error ? <div className="mb-3 text-sm text-red-700 dark:text-red-300">{error}</div> : null}

      <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-left text-gray-600 dark:text-gray-300">
            <tr>
              <th className="px-3 py-2 font-semibold">User</th>
              <th className="px-3 py-2 font-semibold hidden sm:table-cell">Type</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold hidden md:table-cell">Joined</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                  No users found.
                </td>
              </tr>
            ) : (
              data.items.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  className="cursor-pointer border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.shop_name || "-"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell">{u.is_shop ? "Shop" : "Customer"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {u.is_admin ? <Badge tone="purple">Admin</Badge> : null}
                      {u.is_banned ? <Badge tone="red">Banned</Badge> : null}
                      {!u.is_active ? <Badge tone="amber">Unverified</Badge> : null}
                      {u.is_active && !u.is_banned ? <Badge tone="green">Active</Badge> : null}
                      {u.shop_subscription_status === "active" ? <Badge tone="teal">Subscribed</Badge> : null}
                      {u.mfa_enabled ? <Badge tone="blue">2FA</Badge> : null}
                      {/* Ephemeral accounts are throwaway and swept by gc_demo_sessions.py;
                          plain "Demo" is a seeded demo shop, which is permanent. */}
                      {u.is_ephemeral_demo ? (
                        <Badge tone="amber">Demo session</Badge>
                      ) : u.is_demo ? (
                        <Badge tone="gray">Demo</Badge>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-gray-600 dark:text-gray-300">{fmtDate(u.created_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
        <div>
          {data.total} user{data.total === 1 ? "" : "s"} · page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {selectedId ? (
        <UserDetailModal
          authFetch={authFetch}
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      ) : null}
    </div>
  );
}

function UserDetailModal({ authFetch, userId, onClose, onChanged }) {
  const fmtDate = useFmtDate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [banReason, setBanReason] = useState("");
  const [trialDays, setTrialDays] = useState(14);
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }

  const askConfirm = (message, onConfirm) => setConfirm({ message, onConfirm });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/admin/users/${userId}`);
      if (!res.ok) {
        setError("Could not load user.");
        return;
      }
      setUser(await res.json());
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [authFetch, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (path, body) => {
    setBusy(true);
    setError("");
    try {
      const res = await authFetch(`/admin/users/${userId}/${path}`, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.detail || "Action failed.");
        return;
      }
      setUser(await res.json());
      onChanged?.();
    } catch {
      setError("Network error.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-auto" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl mt-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">User detail</h2>
          <button type="button" onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-800">
            ✕
          </button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : !user ? (
          <div className="text-sm text-red-700 dark:text-red-300">{error || "Not found."}</div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.shop_name || "-"}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">{user.email}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                <Badge tone={user.is_shop ? "blue" : "gray"}>{user.is_shop ? "Shop" : "Customer"}</Badge>
                {user.is_admin ? <Badge tone="purple">Admin</Badge> : null}
                {user.is_banned ? <Badge tone="red">Banned</Badge> : null}
                {!user.is_active ? <Badge tone="amber">Unverified</Badge> : null}
                {user.shop_subscription_status === "active" ? <Badge tone="teal">Subscribed</Badge> : null}
                {user.mfa_enabled ? <Badge tone="blue">2FA</Badge> : null}
                {user.shop_subscription_whitelisted ? <Badge tone="green">Whitelisted</Badge> : null}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="text-gray-500 dark:text-gray-400">Phone</div>
              <div className="text-gray-900 dark:text-gray-100">{user.phone_number || "-"}</div>
              <div className="text-gray-500 dark:text-gray-400">Location</div>
              <div className="text-gray-900 dark:text-gray-100">{[user.city, user.state, user.zip_code].filter(Boolean).join(", ") || "-"}</div>
              <div className="text-gray-500 dark:text-gray-400">Subscription</div>
              <div className="text-gray-900 dark:text-gray-100">{user.shop_subscription_status || "-"}</div>
              <div className="text-gray-500 dark:text-gray-400">Joined</div>
              <div className="text-gray-900 dark:text-gray-100">{fmtDate(user.created_at)}</div>
              <div className="text-gray-500 dark:text-gray-400">Last seen</div>
              <div className="text-gray-900 dark:text-gray-100">{fmtDate(user.last_seen_at)}</div>
              <div className="text-gray-500 dark:text-gray-400">Messages / Threads / Work orders</div>
              <div className="text-gray-900 dark:text-gray-100">
                {user.message_count} / {user.thread_count} / {user.work_order_count}
              </div>
              {user.is_banned ? (
                <>
                  <div className="text-gray-500 dark:text-gray-400">Banned at</div>
                  <div className="text-gray-900 dark:text-gray-100">{fmtDate(user.banned_at)}</div>
                  <div className="text-gray-500 dark:text-gray-400">Ban reason</div>
                  <div className="text-gray-900 dark:text-gray-100">{user.ban_reason || "-"}</div>
                </>
              ) : null}
            </div>

            {error ? <div className="text-sm text-red-700 dark:text-red-300">{error}</div> : null}

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              {!user.is_banned ? (
                <div className="space-y-2">
                  <input
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder="Reason for ban (optional)"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      askConfirm("Ban this user? They will be unable to log in and will be hidden from other users.", () =>
                        act("ban", { reason: banReason })
                      )
                    }
                    className="w-full bg-red-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Ban user
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act("unban")}
                  className="w-full bg-emerald-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                >
                  Unban user
                </button>
              )}

              <div className="flex flex-wrap gap-2">
                {user.is_admin ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => act("revoke-admin")}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Revoke admin
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      askConfirm("Grant admin access to this user?", () => act("grant-admin"))
                    }
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Grant admin
                  </button>
                )}
                {!user.is_active ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => act("force-activate")}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Force-activate
                  </button>
                ) : null}
                {user.mfa_enabled ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      askConfirm(
                        "Reset two-factor for this user? They won't be asked for a code at next login and can set it up again from their profile.",
                        () => act("reset-mfa")
                      )
                    }
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Reset 2FA
                  </button>
                ) : null}
              </div>
            </div>

            {user.is_shop ? (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Subscription</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  Status: {user.shop_subscription_status || "-"}
                  {user.shop_trial_ends_at ? ` · trial ends ${fmtDate(user.shop_trial_ends_at)}` : ""}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      askConfirm(
                        user.shop_subscription_whitelisted
                          ? "Remove this shop's free-access whitelist?"
                          : "Whitelist this shop for free access (bypass the subscription requirement)?",
                        () => act("whitelist", { enabled: !user.shop_subscription_whitelisted })
                      )
                    }
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    {user.shop_subscription_whitelisted ? "Remove whitelist" : "Whitelist (free access)"}
                  </button>
                  {user.stripe_customer_id ? (
                    <a
                      href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                    >
                      Open in Stripe ↗
                    </a>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    className="w-20 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-2 text-sm"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      askConfirm(`Extend this shop's trial by ${Number(trialDays) || 0} day(s)?`, () =>
                        act("extend-trial", { days: Number(trialDays) || 0 })
                      )
                    }
                    className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm disabled:opacity-50"
                  >
                    Extend trial (days)
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {confirm ? (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setConfirm(null)}
          >
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{confirm.message}</p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const fn = confirm.onConfirm;
                    setConfirm(null);
                    fn();
                  }}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setConfirm(null)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AuditTab({ authFetch }) {
  const fmtDate = useFmtDate();
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await authFetch(`/admin/audit-log?page=${page}&page_size=${pageSize}`);
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) setData(j);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div>
      <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-left text-gray-600 dark:text-gray-300">
            <tr>
              <th className="px-3 py-2 font-semibold">When</th>
              <th className="px-3 py-2 font-semibold">Admin</th>
              <th className="px-3 py-2 font-semibold">Action</th>
              <th className="px-3 py-2 font-semibold">Target</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-gray-400">
                  No admin actions recorded yet.
                </td>
              </tr>
            ) : (
              data.items.map((a) => (
                <tr key={a.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{a.admin_email || "-"}</td>
                  <td className="px-3 py-2">
                    <Badge tone="blue">{a.action}</Badge>
                    {a.detail ? (
                      <div className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {a.detail.reason ? <div>Reason: {a.detail.reason}</div> : null}
                        {a.detail.note ? <div>Note: {a.detail.note}</div> : null}
                        {a.detail.status ? (
                          <div>
                            Status: {a.detail.status}
                            {a.detail.notified_reporter ? " · emailed reporter" : ""}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200">{a.target_email || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
        <div>
          {data.total} entr{data.total === 1 ? "y" : "ies"} · page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function ThreadViewerModal({ authFetch, threadId, onClose }) {
  const fmtDate = useFmtDate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authFetch(`/admin/threads/${threadId}/messages`);
        if (!res.ok) {
          setError("Could not load conversation.");
          return;
        }
        const j = await res.json();
        if (!cancelled) setMessages(Array.isArray(j) ? j : []);
      } catch {
        if (!cancelled) setError("Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch, threadId]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 overflow-auto"
      onClick={onClose}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl mt-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Conversation (read-only)</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Admin view - full history. This view is logged.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-800">
            ✕
          </button>
        </div>
        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : error ? (
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">No messages.</div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-auto">
            {messages.map((m) => (
              <div key={m.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-2.5">
                <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {m.sender_name || m.sender_email || "-"}
                    {m.sender_name && m.sender_email ? (
                      <span className="font-normal text-gray-400"> · {m.sender_email}</span>
                    ) : null}
                  </span>
                  <span>{fmtDate(m.timestamp)}</span>
                </div>
                <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">{m.content || "-"}</div>
                {m.attachment_count > 0 ? (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    📎 {m.attachment_count} attachment{m.attachment_count === 1 ? "" : "s"}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReportsTab({ authFetch }) {
  const fmtDate = useFmtDate();
  const [status, setStatus] = useState("open");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [viewThread, setViewThread] = useState(null);
  const [resolveTarget, setResolveTarget] = useState(null); // { id, status, reporterEmail }
  const [resolveNote, setResolveNote] = useState("");
  const [resolveNotify, setResolveNotify] = useState(false);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [resolveError, setResolveError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/admin/reports?status=${status}&page=${page}&page_size=${pageSize}`);
      if (!res.ok) {
        setError("Could not load reports.");
        setData({ items: [], total: 0 });
        return;
      }
      setData(await res.json());
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [authFetch, status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const openResolve = (report, newStatus) => {
    setResolveTarget({ id: report.id, status: newStatus, reporterEmail: report.reporter_email });
    setResolveNote("");
    setResolveNotify(false);
    setResolveError("");
  };

  const doResolve = async () => {
    if (!resolveTarget) return;
    setResolveBusy(true);
    setResolveError("");
    try {
      const res = await authFetch(`/admin/reports/${resolveTarget.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({
          status: resolveTarget.status,
          note: resolveNote,
          notify_reporter: resolveNotify,
        }),
      });
      if (res.ok) {
        setResolveTarget(null);
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        setResolveError(data.detail || `Could not update report (HTTP ${res.status}).`);
      }
    } catch {
      setResolveError("Network error updating report.");
    } finally {
      setResolveBusy(false);
    }
  };

  const reopen = async (id) => {
    setBusyId(id);
    setError("");
    try {
      const res = await authFetch(`/admin/reports/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ status: "open" }),
      });
      if (res.ok) {
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || `Could not reopen report (HTTP ${res.status}).`);
      }
    } catch {
      setError("Network error.");
    } finally {
      setBusyId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / pageSize));

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="border border-gray-300 dark:border-gray-600 rounded-xl px-3 py-2 text-sm"
        >
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All</option>
        </select>
      </div>

      {error ? <div className="mb-3 text-sm text-red-700 dark:text-red-300">{error}</div> : null}

      <div className="space-y-2">
        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            No reports.
          </div>
        ) : (
          data.items.map((r) => (
            <div key={r.id} className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="amber">{r.category}</Badge>
                <Badge tone={r.status === "open" ? "blue" : "gray"}>{r.status}</Badge>
                {r.reported_is_banned ? <Badge tone="red">reported user banned</Badge> : null}
                <span className="text-xs text-gray-500 dark:text-gray-400">{fmtDate(r.created_at)}</span>
              </div>
              <div className="mt-2 text-sm text-gray-900 dark:text-gray-100 space-y-0.5">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Reported:</span>{" "}
                  <span className="font-medium">{r.reported_name || "-"}</span>{" "}
                  <span className="text-xs text-gray-500 dark:text-gray-400">{r.reported_email}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">By:</span>{" "}
                  <span className="font-medium">{r.reporter_name || "-"}</span>{" "}
                  <span className="text-xs text-gray-500 dark:text-gray-400">{r.reporter_email}</span>
                </div>
              </div>
              {r.reason ? (
                <div className="mt-1 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">{r.reason}</div>
              ) : null}
              {r.status !== "open" && r.resolution_note ? (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Note: {r.resolution_note}
                  {r.resolved_by_email ? ` - ${r.resolved_by_email}` : ""}
                </div>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-2">
                {r.thread_id ? (
                  <button
                    type="button"
                    onClick={() => setViewThread(r.thread_id)}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
                  >
                    Open conversation
                  </button>
                ) : null}
                {r.status === "open" ? (
                  <>
                    <button
                      type="button"
                      onClick={() => openResolve(r, "resolved")}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm"
                    >
                      Resolve
                    </button>
                    <button
                      type="button"
                      onClick={() => openResolve(r, "dismissed")}
                      className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
                    >
                      Dismiss
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={busyId === r.id}
                    onClick={() => reopen(r.id)}
                    className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm disabled:opacity-50"
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
        <div>
          {data.total} report{data.total === 1 ? "" : "s"} · page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {viewThread ? (
        <ThreadViewerModal authFetch={authFetch} threadId={viewThread} onClose={() => setViewThread(null)} />
      ) : null}

      {resolveTarget ? (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 overflow-auto"
          onClick={() => (resolveBusy ? null : setResolveTarget(null))}
        >
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl mt-10" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {resolveTarget.status === "dismissed" ? "Dismiss report" : "Resolve report"}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Add a note for the record{resolveTarget.reporterEmail ? ", and optionally email it to the reporter" : ""}.
            </p>
            <textarea
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Message / resolution note…"
              className="mt-3 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm"
            />
            {resolveTarget.reporterEmail ? (
              <label className="mt-2 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                  checked={resolveNotify}
                  onChange={(e) => setResolveNotify(e.target.checked)}
                />
                <span>
                  Email this note to the reporter ({resolveTarget.reporterEmail})
                </span>
              </label>
            ) : null}
            {resolveNotify && !resolveNote.trim() ? (
              <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">Add a note to email the reporter.</div>
            ) : null}
            {resolveError ? <div className="mt-2 text-sm text-red-700 dark:text-red-300">{resolveError}</div> : null}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={resolveBusy}
                onClick={() => setResolveTarget(null)}
                className="px-4 py-2 rounded-xl text-sm border border-gray-300 dark:border-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resolveBusy || (resolveNotify && !resolveNote.trim())}
                onClick={doResolve}
                className={cx(
                  "px-4 py-2 rounded-xl text-sm text-white disabled:opacity-50",
                  resolveTarget.status === "dismissed" ? "bg-gray-800" : "bg-emerald-600"
                )}
              >
                {resolveBusy ? "Saving…" : resolveTarget.status === "dismissed" ? "Dismiss" : "Resolve"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SubscriptionsTab({ authFetch }) {
  const fmtDate = useFmtDate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/admin/settings");
      if (!res.ok) {
        setError("Could not load settings.");
        return;
      }
      setSettings(await res.json());
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const setRequired = async (required) => {
    setBusy(true);
    setError("");
    try {
      const res = await authFetch("/admin/settings/payments-required", {
        method: "POST",
        body: JSON.stringify({ required }),
      });
      if (res.ok) {
        setSettings(await res.json());
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Could not update setting.");
      }
    } catch {
      setError("Network error.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Global subscription requirement</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          When ON, shops need an active subscription, a live trial, or a whitelist to appear in Shop
          Explorer and receive new requests. When OFF, all shops are visible regardless of billing.
        </p>
        {error ? <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div> : null}
        {loading ? (
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : settings ? (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Badge tone={settings.payments_required ? "green" : "gray"}>
              {settings.payments_required ? "Required: ON" : "Required: OFF"}
            </Badge>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const next = !settings.payments_required;
                setConfirm({
                  message: next
                    ? "Turn the global subscription requirement ON? Shops without an active subscription, live trial, or whitelist will be hidden from Shop Explorer and stop receiving new requests."
                    : "Turn the global subscription requirement OFF? All shops will be visible regardless of billing.",
                  onConfirm: () => setRequired(next),
                });
              }}
              className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm disabled:opacity-50"
            >
              {busy ? "Saving…" : settings.payments_required ? "Turn OFF" : "Turn ON"}
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              source: {settings.payments_required_source}
              {settings.payments_required_updated_at
                ? ` · updated ${fmtDate(settings.payments_required_updated_at)}`
                : ""}
            </span>
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-sm text-gray-600 dark:text-gray-300">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Per-shop controls</h2>
        <p className="mt-1">
          To whitelist a shop (free access), extend a trial, or open it in Stripe, go to{" "}
          <span className="font-medium text-gray-800 dark:text-gray-100">Users</span>, open a shop account, and use the
          Subscription section.
        </p>
      </div>

      {confirm ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirm(null)}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{confirm.message}</p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  const fn = confirm.onConfirm;
                  setConfirm(null);
                  fn();
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-check not-italic" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
              >
                <i className="fi fi-rr-cross not-italic" />
                No
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={cx("mt-1 text-2xl font-bold", tone || "text-gray-900 dark:text-gray-100")}>{value}</div>
    </div>
  );
}

function DashboardTab({ authFetch }) {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authFetch("/admin/metrics");
        if (!res.ok) {
          setError("Could not load metrics.");
          return;
        }
        const j = await res.json();
        if (!cancelled) setM(j);
      } catch {
        if (!cancelled) setError("Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  if (loading) return <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>;
  if (error) return <div className="text-sm text-red-700 dark:text-red-300">{error}</div>;
  if (!m) return null;

  const wo = m.work_orders_by_status || {};
  const woOrder = [
    "initiated",
    "pending",
    "accepted",
    "quoted",
    "scheduled",
    "in_progress",
    "completed",
    "closed",
    "rejected",
  ];
  const woShown = woOrder.filter((s) => wo[s]);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Users</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Stat label="Total" value={m.users_total} />
          <Stat label="Shops" value={m.shops_total} />
          <Stat label="Customers" value={m.customers_total} />
          <Stat label="Banned" value={m.banned_total} tone={m.banned_total ? "text-red-700 dark:text-red-300" : undefined} />
          <Stat label="Admins" value={m.admins_total} />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Growth &amp; subscriptions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Stat label="Signups (7d)" value={m.signups_7d} />
          <Stat label="Signups (30d)" value={m.signups_30d} />
          <Stat label="Active subs" value={m.subs_active} />
          <Stat label="Trialing" value={m.subs_trialing} />
          <Stat label="Whitelisted" value={m.subs_whitelisted} />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Activity</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Messages" value={m.messages_total} />
          <Stat label="Work orders" value={m.work_orders_total} />
          <Stat label="Open reports" value={m.reports_open} tone={m.reports_open ? "text-amber-700 dark:text-amber-300" : undefined} />
          <Stat label="Incomplete shop profiles" value={m.shops_incomplete} />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Work orders by status</h3>
        <div className="flex flex-wrap gap-2">
          {woShown.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">No work orders.</div>
          ) : (
            woShown.map((s) => (
              <div key={s} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm">
                <span className="text-gray-500 dark:text-gray-400">{s.replace("_", " ")}:</span>{" "}
                <span className="font-semibold text-gray-900 dark:text-gray-100">{wo[s]}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function HealthDot({ ok }) {
  return <Badge tone={ok ? "green" : "red"}>{ok ? "OK" : "Not configured"}</Badge>;
}

function OpsTab({ authFetch }) {
  const fmtDate = useFmtDate();
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [hRes, sRes] = await Promise.all([
          authFetch("/admin/health"),
          authFetch("/admin/scheduler"),
        ]);
        if (hRes.ok && !cancelled) setHealth(await hRes.json());
        if (sRes.ok && !cancelled) setJobs(await sRes.json());
        if ((!hRes.ok || !sRes.ok) && !cancelled) setError("Could not load some ops data.");
      } catch {
        if (!cancelled) setError("Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const rows = [
    ["Database", health?.database],
    ["Object storage", health?.object_storage],
    ["Web push", health?.push],
    ["Stripe", health?.stripe],
    ["Email (Postmark)", health?.email],
  ];

  return (
    <div className="space-y-4">
      {error ? <div className="text-sm text-red-700 dark:text-red-300">{error}</div> : null}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">System health</h3>
        {loading ? (
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : health ? (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {rows.map(([label, ok]) => (
              <div key={label} className="flex items-center justify-between">
                <span className="text-gray-700 dark:text-gray-200">{label}</span>
                <HealthDot ok={!!ok} />
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Scheduler - last activity</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Most recent run of each job - last-run time and success. Falls back to best-effort signals when the run log is empty.</p>
        {loading ? (
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : jobs.length === 0 ? (
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">No data.</div>
        ) : (
          <div className="mt-3 space-y-0.5 text-sm">
            {jobs.map((j) => (
              <div key={j.name} className="flex items-center justify-between gap-3 border-t border-gray-100 dark:border-gray-700 py-1.5">
                <span className="flex items-center gap-2 min-w-0 text-gray-700 dark:text-gray-200">
                  {typeof j.ok === "boolean" ? <HealthDot ok={j.ok} /> : null}
                  <span className="truncate">{j.name}</span>
                </span>
                <span className={`text-right whitespace-nowrap ${j.ok === false ? "text-red-600 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>
                  {j.last_at ? fmtDate(j.last_at) : (j.detail || "-")}
                  {j.ok === false && j.detail ? <span className="block text-xs">{j.detail}</span> : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
